import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type InteractionMode = 'pointer' | 'touch';

interface InteractionContextValue {
  mode: InteractionMode;
  isTouchDevice: boolean;
  // Feature flags derived from mode
  canDragEvents: boolean;
  canDragToCreate: boolean;
  canHover: boolean;
}

const InteractionContext = createContext<InteractionContextValue | undefined>(undefined);

interface InteractionProviderProps {
  children: ReactNode;
  // Allow override for testing
  forceMode?: InteractionMode;
}

/**
 * Detects interaction mode based on device capabilities.
 * Uses feature detection rather than user agent sniffing.
 */
function detectInteractionMode(): InteractionMode {
  // Check if we're in a browser environment
  if (typeof window === 'undefined') {
    return 'pointer';
  }

  // Primary detection: CSS media query for pointer type
  // 'coarse' = finger/touch, 'fine' = mouse/trackpad/stylus
  const hasCoarsePointer = window.matchMedia('(pointer: coarse)').matches;
  const hasFinePointer = window.matchMedia('(pointer: fine)').matches;

  // Secondary: hover capability
  // Devices without hover are typically touch-only
  const canHover = window.matchMedia('(hover: hover)').matches;

  // Tertiary: touch event support (fallback)
  const hasTouchSupport = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  // Decision logic:
  // - If device has fine pointer AND can hover → pointer mode (desktop/laptop)
  // - If device has only coarse pointer OR cannot hover → touch mode (phone/tablet)
  // - Hybrid devices (Surface, iPad with trackpad):
  //   - If they have fine pointer capability, prefer pointer mode
  //   - This allows mouse/trackpad users on tablets to use desktop flow

  if (hasFinePointer && canHover) {
    return 'pointer';
  }

  if (hasCoarsePointer && !hasFinePointer) {
    return 'touch';
  }

  // Fallback for edge cases
  if (hasTouchSupport && !canHover) {
    return 'touch';
  }

  return 'pointer';
}

export function InteractionProvider({ children, forceMode }: InteractionProviderProps) {
  const [mode, setMode] = useState<InteractionMode>(() => {
    if (forceMode) return forceMode;
    return detectInteractionMode();
  });

  useEffect(() => {
    if (forceMode) {
      setMode(forceMode);
      return;
    }

    // Listen for changes in pointer/hover capabilities
    // This handles cases like:
    // - Tablet user connects a mouse
    // - Desktop user in tablet mode
    const coarseQuery = window.matchMedia('(pointer: coarse)');
    const fineQuery = window.matchMedia('(pointer: fine)');
    const hoverQuery = window.matchMedia('(hover: hover)');

    const handleChange = () => {
      setMode(detectInteractionMode());
    };

    // Modern browsers
    coarseQuery.addEventListener?.('change', handleChange);
    fineQuery.addEventListener?.('change', handleChange);
    hoverQuery.addEventListener?.('change', handleChange);

    return () => {
      coarseQuery.removeEventListener?.('change', handleChange);
      fineQuery.removeEventListener?.('change', handleChange);
      hoverQuery.removeEventListener?.('change', handleChange);
    };
  }, [forceMode]);

  const value: InteractionContextValue = {
    mode,
    isTouchDevice: mode === 'touch',
    // Feature flags
    canDragEvents: mode === 'pointer',
    canDragToCreate: mode === 'pointer',
    canHover: mode === 'pointer',
  };

  return (
    <InteractionContext.Provider value={value}>
      {children}
    </InteractionContext.Provider>
  );
}

export function useInteraction(): InteractionContextValue {
  const context = useContext(InteractionContext);
  if (context === undefined) {
    // Provide sensible defaults if used outside provider
    // This allows gradual adoption
    return {
      mode: 'pointer',
      isTouchDevice: false,
      canDragEvents: true,
      canDragToCreate: true,
      canHover: true,
    };
  }
  return context;
}

// Export for testing
export { InteractionContext };
